import type { Guide, GuideLocaleContent } from '@/lib/data/guides/types';
import { getGuideCategoryLabel, getGuideContent, getRelatedGuides } from '@/lib/data/guides';

import { StoneFAQ } from './StoneFAQ';
import { StoneGuideCTA } from './StoneGuideCTA';
import { StoneGuideContent } from './StoneGuideContent';
import { StoneGuideHero } from './StoneGuideHero';
import { StoneGuideRelated } from './StoneGuideRelated';
import { StoneGuideTOC } from './StoneGuideTOC';

export interface StoneGuidePageProps {
  guide: Guide;
  locale: string;
  guides?: Guide[];
  className?: string;
}

export function StoneGuidePage({
  guide,
  locale,
  className = '',
}: StoneGuidePageProps) {
  const content: GuideLocaleContent = getGuideContent(guide, locale);
  const categoryLabel = getGuideCategoryLabel(guide.category, locale);
  const relatedGuides = getRelatedGuides(guide, locale).map((related) => ({
    id: related.id,
    slug: related.content.slug,
    title: related.content.title,
    description: related.content.description,
    categoryLabel: getGuideCategoryLabel(related.category, locale),
  }));

  return (
    <div className={`font-sans text-stone-900 bg-stone-50 min-h-screen ${className}`}>
      <StoneGuideHero
        title={content.title}
        categoryLabel={categoryLabel}
        category={guide.category}
        locale={locale}
      />

      <div className="container mx-auto max-w-7xl px-6 md:px-12 -mt-16 relative z-10 pb-8">
        <div className="bg-white p-8 md:p-12 shadow-lg mb-12">
          <p
            className="font-sans text-stone-600 font-light leading-relaxed text-lg max-w-3xl"
            data-speakable
          >
            {content.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 pb-16">
          <div className="lg:col-span-8">
            <StoneGuideContent sections={content.sections} />
          </div>

          <div className="lg:col-span-4">
            <StoneGuideTOC
              sections={content.sections.map((section) => ({
                id: section.id,
                heading: section.heading,
              }))}
              readingTime={content.readingTime}
              lastUpdated={content.lastUpdated}
              locale={locale}
            />
          </div>
        </div>
      </div>

      {content.faqs && content.faqs.length > 0 ? (
        <StoneFAQ faqs={content.faqs} locale={locale} className="bg-white" />
      ) : null}

      <StoneGuideRelated guides={relatedGuides} locale={locale} />
      <StoneGuideCTA locale={locale} />
    </div>
  );
}

export default StoneGuidePage;
